// Client storage adapter: localStorage + optional Netlify sync
// Non-blocking, debounced write-through; last-write-wins by updatedAt

const ENDPOINT = "/.netlify/functions/progress"
const LS_KEY = 'lp.progress'
const TS_KEY = 'lp.progress.updatedAt'
const DEBOUNCE_MS = 300

let debounceTimer = null
let lastSyncPromise = null
let preferRemoteOnce = true
const subscribers = new Set()

function notify(data) {
  try {
    for (const cb of subscribers) {
      try { cb(data) } catch {}
    }
  } catch {}
}

export function getLocalProgress() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}') } catch { return {} }
}
export function setLocalProgress(data) {
  localStorage.setItem(LS_KEY, JSON.stringify(data || {}))
  // Do NOT touch TS here; TS is server-authoritative and set only on remote ack or remote apply
  notify(data || {})
}

function setLocalProgressSilent(data) {
  localStorage.setItem(LS_KEY, JSON.stringify(data || {}))
  // no TS update here either
}

function setLocalTimestamp(ts) {
  if (ts) localStorage.setItem(TS_KEY, ts)
}

function applyRemote(data, updatedAt) {
  localStorage.setItem(LS_KEY, JSON.stringify(data || {}))
  setLocalTimestamp(updatedAt)
  // Same-tab notify and cross-tab storage event
  notify(data || {})
  window.dispatchEvent(new StorageEvent('storage', { key: LS_KEY, newValue: JSON.stringify(data || {}) }))
}
export function getLocalUpdatedAt() {
  return localStorage.getItem(TS_KEY) || null
}

export async function fetchRemote() {
  try {
    const res = await fetch(ENDPOINT, { method: 'GET' })
    if (!res.ok) throw new Error(`GET ${ENDPOINT} ${res.status}`)
    const json = await res.json()
    return { data: json?.data || {}, updatedAt: json?.updatedAt || null }
  } catch (e) {
    return { data: null, updatedAt: null, error: e }
  }
}

export async function pushRemote(data, updatedAt) {
  try {
    const res = await fetch(ENDPOINT, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ data, updatedAt }),
    })
    if (!res.ok) throw new Error(`PUT ${ENDPOINT} ${res.status}`)
    const json = await res.json()
    if (json?.updatedAt) {
      // Align local timestamp to server authoritative updatedAt
      setLocalTimestamp(json.updatedAt)
    }
    return json
  } catch (e) {
    return { error: e }
  }
}

export async function getProgress() {
  // Strategy: load local immediately; try remote in background and reconcile
  const local = getLocalProgress()
  const localTs = getLocalUpdatedAt()

  // Fire and forget remote check
  lastSyncPromise = (async () => {
    const remote = await fetchRemote()
    if (preferRemoteOnce) {
      // Prefer remote snapshot on first load if it has any updatedAt; otherwise keep local (no background push)
      if (remote?.updatedAt) {
        applyRemote(remote.data || {}, remote.updatedAt)
      }
      preferRemoteOnce = false
      return
    }
    // Subsequent reconciles: last-write-wins (by timestamp)
    if (remote?.data && (remote.updatedAt && (!localTs || remote.updatedAt >= localTs))) {
      applyRemote(remote.data, remote.updatedAt)
    } // else: keep local; do not push on reload
  })()

  return local
}

export function setProgress(data) {
  const now = new Date().toISOString()
  // Update local without notifying same-tab subscribers (component already updated its state)
  setLocalProgressSilent(data)
  // debounce remote write
  if (debounceTimer) clearTimeout(debounceTimer)
  debounceTimer = setTimeout(() => {
    void pushRemote(data, now)
  }, DEBOUNCE_MS)
}

export function onProgressChange(cb) {
  // Subscribe for same-tab updates
  subscribers.add(cb)
  // Also listen for cross-tab storage events
  const handler = (e) => {
    if (e.key === LS_KEY) cb(getLocalProgress())
  }
  window.addEventListener('storage', handler)
  return () => {
    subscribers.delete(cb)
    window.removeEventListener('storage', handler)
  }
}

export function whenRemoteSettled() {
  return lastSyncPromise || Promise.resolve()
}
